/**
 * Connector post-wipe / migration rebuild is a healthy in-progress state
 * (`library_rebuilding`). UI must show "Rebuilding library…" — never the
 * ops-banner "a command is stuck — reset" path (#516 / connector PR #74).
 */

export const LIBRARY_REBUILDING_CODE = 'library_rebuilding';

export const LIBRARY_REBUILDING_USER_MESSAGE = 'Rebuilding library…';

export type LibraryRebuildStatus = {
  rebuilding?: boolean | null;
  code?: string | null;
  message?: string | null;
  status?: string | null;
  migrationPending?: boolean | null;
};

let _rebuildHealthy = false;
const _listeners = new Set<(active: boolean) => void>();

export function isLibraryRebuildHealthy(): boolean {
  return _rebuildHealthy;
}

export function noteLibraryRebuilding(active: boolean): void {
  if (_rebuildHealthy === active) return;
  _rebuildHealthy = active;
  _listeners.forEach((cb) => {
    try { cb(active); } catch { /* ignore */ }
  });
}

export function subscribeLibraryRebuilding(cb: (active: boolean) => void): () => void {
  _listeners.add(cb);
  return () => { _listeners.delete(cb); };
}

export function resetLibraryRebuildingForTests(): void {
  _rebuildHealthy = false;
  _listeners.clear();
}

export function isLibraryRebuildingStatus(status: LibraryRebuildStatus | null | undefined): boolean {
  if (!status) return false;
  if (status.rebuilding === true) return true;
  const code = String(status.code ?? '').trim();
  return code === LIBRARY_REBUILDING_CODE;
}

export function isLibraryRebuildingError(error: unknown): boolean {
  const text = error instanceof Error
    ? `${error.message} ${error.name}`
    : String(error ?? '');
  return text.includes(LIBRARY_REBUILDING_CODE);
}

export function applyLibraryRebuildFromStatus(
  status: LibraryRebuildStatus | null | undefined,
): boolean {
  const active = isLibraryRebuildingStatus(status);
  noteLibraryRebuilding(active);
  return active;
}

export function applyLibraryRebuildFromError(error: unknown): boolean {
  const active = isLibraryRebuildingError(error);
  if (active) noteLibraryRebuilding(true);
  return active;
}
